import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApi } from '@/shared/api';
import { useObjectScope } from '@/shared/object-scope';
import type { AdvancedSearchRequest, SearchFilterGroup, SearchSort } from '@/shared/api';
import { SEARCH_SCHEMA_VERSION } from '@/shared/api';
import { buildRequest, emptyGroup, normalizeGroup } from './filterModel';
import { useAdvancedCatalog } from './useAdvancedCatalog';
import { useSavedSearches } from './useSavedSearches';
import { downloadBlob } from './downloadBlob';

const PAGE_SIZE = 50;

export function useAdvancedSearchState() {
  const api = useApi();
  const { propertyIds } = useObjectScope();
  const catalog = useAdvancedCatalog();
  const saved = useSavedSearches();

  const [dataset, setDataset] = useState('');
  const [group, setGroup] = useState<SearchFilterGroup>(emptyGroup());
  const [columns, setColumns] = useState<string[]>([]);
  const [sort, setSort] = useState<SearchSort[]>([]);
  const [ran, setRan] = useState<AdvancedSearchRequest | null>(null);
  /**
   * Solange eine Vorlage geladen ist, laeuft die Ausfuehrung ueber `saved.run` statt ueber die
   * vom Client zusammengebaute Anfrage. Der Grund ist die SchemaVersion: der Server nimmt die
   * **gespeicherte**, `buildRequest` kennt nur die aktuelle des Clients. Heute sind beide gleich;
   * mit der ersten Schema-Erhoehung waere eine alte Vorlage sonst stillschweigend als neu
   * deklariert. `ran` haelt in diesem Fall nur Seite und Seitengroesse.
   */
  const [savedId, setSavedId] = useState<string | null>(null);

  const activeDataset = catalog.data?.datasets.find((d) => d.key === dataset) ?? null;

  const results = useQuery({
    queryKey: ['search', 'advanced', savedId, ran],
    queryFn: ({ signal }) =>
      savedId
        ? api.search.saved.run(savedId, {
            page: ran!.page,
            pageSize: ran!.pageSize,
            propertyIds: propertyIds ?? null,
          })
        : api.search.advanced(ran!, signal),
    enabled: ran !== null,
  });

  const changeDataset = (key: string) => {
    setDataset(key);
    setGroup(emptyGroup());
    setColumns([]);
    setSort([]);
    setRan(null);
    setSavedId(null);
  };

  const run = () => {
    if (!dataset) return;
    // Ein eigener Lauf loest die Bindung an die Vorlage: gesucht wird, was im Formular steht.
    setSavedId(null);
    setRan(
      buildRequest({ dataset, group, columns, sort, page: 1, pageSize: PAGE_SIZE, propertyIds }),
    );
  };

  const changePage = (page: number) => setRan((prev) => (prev ? { ...prev, page } : prev));

  const loadSaved = async (id: string) => {
    const detail = await api.search.saved.get(id);
    const nextGroup = detail.filters ?? emptyGroup();
    const nextColumns = detail.columns ?? [];
    const nextSort = detail.sort ?? [];
    setDataset(detail.dataset);
    setGroup(nextGroup);
    setColumns(nextColumns);
    setSort(nextSort);
    setSavedId(id);
    // Das Formular zeigt die Definition, ausgefuehrt wird sie serverseitig — `ran` traegt hier
    // nur Seite und Seitengroesse in die Abfrage.
    setRan(
      buildRequest({
        dataset: detail.dataset,
        group: nextGroup,
        columns: nextColumns,
        sort: nextSort,
        page: 1,
        pageSize: detail.pageSize || PAGE_SIZE,
        propertyIds,
      }),
    );
  };

  const saveCurrent = (name: string) => {
    if (!dataset) return;
    saved.create.mutate(
      {
        name,
        dataset,
        filters: normalizeGroup(group),
        columns: columns.length > 0 ? columns : null,
        sort: sort.length > 0 ? sort : null,
        pageSize: PAGE_SIZE,
      },
      {
        onSuccess: () => toast.success('Vorlage gespeichert'),
        onError: () => toast.error('Speichern fehlgeschlagen'),
      },
    );
  };

  const removeSaved = (id: string) => saved.remove.mutate(id);

  const exportAs = async (format: 'csv' | 'xlsx') => {
    if (!ran) return;
    try {
      // Derselbe Grund wie beim Lauf: aus einer Vorlage exportiert der Server ihre gespeicherte
      // Definition, sonst waere der Export etwas anderes als das, was auf dem Schirm steht.
      const blob = savedId
        ? await api.search.saved.export(
            savedId,
            { page: ran.page, pageSize: ran.pageSize, propertyIds: propertyIds ?? null },
            format,
          )
        : await api.search.export(ran, format);
      downloadBlob(blob, `${dataset || 'export'}.${format}`);
    } catch {
      toast.error('Export fehlgeschlagen');
    }
  };

  return {
    catalog,
    saved,
    dataset,
    activeDataset,
    group,
    setGroup,
    columns,
    setColumns,
    sort,
    setSort,
    ran,
    results,
    schemaVersion: SEARCH_SCHEMA_VERSION,
    changeDataset,
    run,
    changePage,
    loadSaved,
    saveCurrent,
    removeSaved,
    exportAs,
  };
}
